import React, { useMemo } from 'react';
import { PublicSetCardAll } from 'store/services/sets/publicSetsApi';
import { buildCardUrl } from 'utils/cardUrlUtils';
import { ManaCost } from 'components/ManaCost/ManaCost';
import { rarityColors, formatRarity, parseCollectorNumber } from '../setCardsShared';
import './SetCardsList.css';

interface SetCardsListProps {
  cards: PublicSetCardAll[];
  setCode?: string;
}

// Format USD price - show "???" for null per CLAUDE.md rules
const formatUsd = (usd: number | null): string => {
  if (usd === null) return '???';
  return `$${usd.toFixed(2)}`;
};

const SetCardsList: React.FC<SetCardsListProps> = ({ cards, setCode }) => {
  const sortedCards = useMemo(() => {
    return [...cards].sort((a, b) => {
      const aNum = parseCollectorNumber(a.collectorNumber);
      const bNum = parseCollectorNumber(b.collectorNumber);
      if (aNum.numeric !== bNum.numeric) {
        return aNum.numeric - bNum.numeric;
      }
      return aNum.suffix.localeCompare(bNum.suffix);
    });
  }, [cards]);

  return (
    <ul className="set-cards-list">
      {sortedCards.map((card) => {
        const rarityColor = rarityColors[card.rarity?.toLowerCase()] || rarityColors.common;
        const hasPT = card.power != null && card.toughness != null;

        return (
          <li key={card.scryfallID} className="set-cards-list-item">
            <div className="set-cards-list-item-header">
              <a href={buildCardUrl({ ...card, setCode })} className="set-cards-list-name">
                {card.name}
              </a>
              <ManaCost manaCost={card.manaCost} size="small" className="set-cards-list-mana" />
            </div>
            {card.typeLine && (
              <div className="set-cards-list-typeline">{card.typeLine}</div>
            )}
            {card.oracleText && (
              <div className="set-cards-list-oracle">{card.oracleText}</div>
            )}
            {card.flavorText && (
              <em className="set-cards-list-flavor">{card.flavorText}</em>
            )}
            <div className="set-cards-list-meta">
              {hasPT && (
                <span className="set-cards-list-pt">
                  {card.power}/{card.toughness}
                </span>
              )}
              <span
                className="set-cards-list-rarity-badge"
                style={{ backgroundColor: rarityColor }}
              >
                {formatRarity(card.rarity)}
              </span>
              <span className="set-cards-list-collector">#{card.collectorNumber}</span>
              <span className="set-cards-list-price">{formatUsd(card.usd)}</span>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default SetCardsList;
